/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { IKeyValue } from "../components/Administrations/types";
import { invalidStr } from "../globals/constants";
import { titleCase } from "./helpers";

interface IData {
  [key: string]: any;
}

export const validate = (data: IData) => {
  let errorMessages = {};
  //const omitKeys = ["description", "operState", "contact", "location"];
  Object.entries(data).forEach(([key, value]) => {
    if (value === "" || value === null || value === undefined) {
      errorMessages = { ...errorMessages, [key]: `${titleCase(key)} is required !` };
    }
  });

  return { errorMessages, isValid: Object.keys(errorMessages).length === 0 };
};

export const isIPv4 = (value: string | undefined) => !!value?.match(/^(([1-9]?\d|1\d\d|25[0-5]|2[0-4]\d)\.){3}([1-9]?\d|1\d\d|25[0-5]|2[0-4]\d)$/);

export const isIPv6 = (value: string | undefined) =>
  !!value?.match(
    /^\s*((([0-9A-Fa-f]{1,4}:){7}(([0-9A-Fa-f]{1,4})|:))|(([0-9A-Fa-f]{1,4}:){6}(:|((25[0-5]|2[0-4]\d|[01]?\d{1,2})(\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})){3})|(:[0-9A-Fa-f]{1,4})))|(([0-9A-Fa-f]{1,4}:){5}((:((25[0-5]|2[0-4]\d|[01]?\d{1,2})(\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})){3})?)|((:[0-9A-Fa-f]{1,4}){1,2})))|(([0-9A-Fa-f]{1,4}:){4}(:[0-9A-Fa-f]{1,4}){0,1}((:((25[0-5]|2[0-4]\d|[01]?\d{1,2})(\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})){3})?)|((:[0-9A-Fa-f]{1,4}){1,2})))|(([0-9A-Fa-f]{1,4}:){3}(:[0-9A-Fa-f]{1,4}){0,2}((:((25[0-5]|2[0-4]\d|[01]?\d{1,2})(\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})){3})?)|((:[0-9A-Fa-f]{1,4}){1,2})))|(([0-9A-Fa-f]{1,4}:){2}(:[0-9A-Fa-f]{1,4}){0,3}((:((25[0-5]|2[0-4]\d|[01]?\d{1,2})(\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})){3})?)|((:[0-9A-Fa-f]{1,4}){1,2})))|(([0-9A-Fa-f]{1,4}:)(:[0-9A-Fa-f]{1,4}){0,4}((:((25[0-5]|2[0-4]\d|[01]?\d{1,2})(\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})){3})?)|((:[0-9A-Fa-f]{1,4}){1,2})))|(:(:[0-9A-Fa-f]{1,4}){0,5}((:((25[0-5]|2[0-4]\d|[01]?\d{1,2})(\.(25[0-5]|2[0-4]\d|[01]?\d{1,2})){3})?)|((:[0-9A-Fa-f]{1,4}){1,2}))))(%.+)?\s*$/
  );
export const isIPv4SubnetMask = (value: string | undefined) =>
  !!value?.match(
    /^(((255\.){3}(255|254|252|248|240|224|192|128|0))|((255\.){2}(255|254|252|248|240|224|192|128|0)\.0)|((255\.)(255|254|252|248|240|224|192|128|0)(\.0){2})|((255|254|252|248|240|224|192|128|0)(\.0){3}))$/
  );
export const isIPv6SubnetMask = (value: string | undefined) =>
  !!value && !!value.trim() && Number.isInteger(Number(value)) ? parseInt(value) >= 1 && parseInt(value) <= 128 : false;

export const isValidHostName = (value: string | undefined) => !!value && value?.match(/^(?![0-9]+$)(?!-)[a-zA-Z0-9-]{1,63}(?<!-)(\.[a-zA-Z]{2,})+$/);

export const isServer = (value: string | undefined) =>
  !!value?.match(/^(([a-zA-Z]|[a-zA-Z0-9][a-zA-Z0-9\\-]*[a-zA-Z0-9])\\.)*([a-zA-Z]|[A-Za-z0-9][A-Za-z0-9\\-]*[A-Za-z0-9])$/);

export const isValidEmail = (value: string | undefined) =>
  !!value && value?.match(/^(?!.{149})[a-zA-Z0-9._%+-]+@(?!.*?\.\.)[a-zA-Z0-9.-]+[.][a-zA-Z]{2,}$|(^$)$/);

export const isValidDigit = (value: string | undefined): boolean => !!value?.match(/^[0-9]*$/);

export const isValidServerPort = (value: string | undefined): boolean =>
  !!value && !isNaN(parseInt(value)) && parseInt(value || "0") >= 1 && parseInt(value || "0") <= 65535;

export const isValidProfileAttributeType = (value: string | undefined) =>
  !!value && !isNaN(parseInt(value)) ? parseInt(value) >= 1 && parseInt(value) <= 255 : true;

export const isValidPassword = (value: string | undefined) => !!value && value?.match(/^(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&*!])[A-Za-z\d@#$%^&*!]{8,}$/);

export const isValidFile = (value: string | undefined) => !!value && value?.match(/^[a-zA-Z0-9_]+$/);

export const validatePhoneNumber = (phoneNumber: any) => {
  const regex = /^(\+\d{2,4})?\d{10}$/;
  const isValid = regex.test(phoneNumber);
  return isValid;
};

const specialCharsRegx = /[ `!@#$%^&*()_+\-=\\[\]{};':"\\|,.<>\\/?~]/;
export const isSpecialChars = (value: string | undefined) => !!value && specialCharsRegx.test(value);

export const validateIP = (value: string) => {
  const { ipAddress } = invalidStr;
  let isValidIP: boolean;
  let errorMessages: string;
  if (!!value) {
    isValidIP = isIPv4(value) || isIPv6(value) || isServer(value);
    errorMessages = !isValidIP ? ipAddress.invalid : "";
  } else {
    isValidIP = false;
    errorMessages = ipAddress.required;
  }
  return { isValidIP, errorMessages };
};

export const validateSubnet = (value: string) => {
  const { subnet } = invalidStr;
  let isValidSubnet: boolean;
  let errorMessages: string;
  if (!!value) {
    isValidSubnet = isIPv4SubnetMask(value);
    errorMessages = !isValidSubnet ? subnet.invalid : "";
  } else {
    isValidSubnet = false;
    errorMessages = subnet.required;
  }
  return { isValidSubnet, errorMessages };
};

export const isError = (errorData: IKeyValue) => {
  if (!!errorData) {
    return Object.values(errorData).filter(Boolean).length > 0;
  } else {
    return false;
  }
};
